import axios from 'axios';
import { useMsal } from "@azure/msal-react";

const clientId = import.meta.env.VITE_CLIENT_ID;
// DNA APIM validate-jwt expects aud = api://{clientId} (ACAA_API_SCOPE).
// Prefer VITE_API_SCOPE; fall back to clientId/.default for local/legacy.
const apiScope = import.meta.env.VITE_API_SCOPE || `${clientId}/.default`;

const useAxiosInstance = () => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();

    const axiosInstance = axios.create({
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const getToken = async () => {
        if (!activeAccount) return null;
        const response = await instance.acquireTokenSilent({
            scopes: [apiScope],
            account: activeAccount,
        });
        return response.accessToken;
    };

    axiosInstance.interceptors.request.use(async (request) => {
        try {
            const accessToken = await getToken();
            if (accessToken) {
                localStorage.setItem('accessToken', accessToken);
                request.headers.Authorization = `Bearer ${accessToken}`;
            }
        } catch (error) {
            console.error('Failed to acquire access token:', error);
            const cached = localStorage.getItem('accessToken');
            if (cached) {
                request.headers.Authorization = `Bearer ${cached}`;
            }
        }
        return request;
    }, (error) => Promise.reject(error));

    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;
            if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
                originalRequest._retry = true;
                try {
                    const token = await getToken();
                    if (!token) return Promise.reject(error);
                    localStorage.setItem('accessToken', token);
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axiosInstance(originalRequest);
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                    localStorage.removeItem('accessToken');
                    return Promise.reject(refreshError);
                }
            }
            return Promise.reject(error);
        }
    );

    return axiosInstance;
};

export default useAxiosInstance;
